import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  useRequest,
  type MutationFunction,
  type QueryFunction,
} from '../common';
import {
  getWebhookDetailsKey,
  getWebhookListKey,
  getWebhookSecretKey,
  getWebhookDispatchesListKey,
  getWebhookDispatchesDetailsKey,
} from './keys';

//Fetches single webhook per UUID
export const useWebhook: QueryFunction<
  unknown,
  [uuid: string],
  ReturnType<typeof getWebhookDetailsKey>
> = (uuid, { query, requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);

  return useQuery({
    ...queryOptions,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- Fix in PP-24289
    queryKey: getWebhookDetailsKey(uuid),
    queryFn: ({ signal }) =>
      request(`/managed_integrations/webhook/${uuid}`, {
        query,
        init: { signal },
      }),
  });
};

//Fetches entire list of available webhooks
export const useWebhookList: QueryFunction<
  any[],
  [managedIntegrationUuid: string],
  ReturnType<typeof getWebhookListKey>
> = (managedIntegrationUuid, { query, requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);

  return useQuery({
    ...queryOptions,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- Fix in PP-24289
    queryKey: getWebhookListKey(),
    queryFn: ({ signal }) =>
      request(`/managed_integrations/${managedIntegrationUuid}/webhook`, {
        query,
        init: { signal },
      }),
  });
};

//Fetches a webhook's secret per uuid
export const useWebhookSecret: QueryFunction<
  unknown,
  [uuid: string],
  ReturnType<typeof getWebhookSecretKey>
> = (uuid, { query, requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);

  return useQuery({
    ...queryOptions,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- Fix in PP-24289
    queryKey: getWebhookSecretKey(uuid),
    queryFn: ({ signal }) =>
      request(`/managed_integrations/webhook/${uuid}/signing_secret`, {
        query,
        init: { signal },
      }),
  });
};

//Fetches a webhook's dispatches per uuid
export const useWebhookDispatches: QueryFunction<
  any[],
  [uuid: string],
  ReturnType<typeof getWebhookDispatchesListKey>
> = (uuid, { query, requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);

  return useQuery({
    ...queryOptions,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- Fix in PP-24289
    queryKey: getWebhookDispatchesListKey(/*uuid*/),
    queryFn: ({ signal }) =>
      request(`/managed_integrations/webhook/${uuid}/dispatches`, {
        query,
        init: { signal },
      }),
  });
};

//Fetches a webhook's dispatches per uuid
export const useDispatchDetails: QueryFunction<
  unknown,
  [uuid: string],
  ReturnType<typeof getWebhookDispatchesDetailsKey>
> = (uuid, { query, requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);
  return useQuery({
    ...queryOptions,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- Fix in PP-24289
    queryKey: getWebhookDispatchesDetailsKey(/*uuid*/),
    queryFn: ({ signal }) =>
      request(`/events/dispatches/${uuid}`, {
        query,
        init: { signal },
      }),
  });
};

//This sends a dispatch, overwrites QueryData for that individual webhook and dispatch,
//and invalidates all related webhook and dispatch list queries
//Requires an event_id and webhook_id in the body
export const useSendDispatch: MutationFunction<unknown, { body: any }> = ({
  query,
  requestOptions,
  queryOptions,
} = {}) => {
  const queryClient = useQueryClient();
  const request = useRequest(requestOptions);

  return useMutation(
    ({ body }) => {
      return request(`/events/dispatches`, {
        query,
        init: { method: 'POST', body },
      });
    },
    {
      ...queryOptions,
      mutationKey: ['sendDispatch'],
      onSuccess: (data, requestData, ...rest) => {
        // queryClient.invalidateQueries(getWebhookListKey());
        queryClient.invalidateQueries({
          queryKey: getWebhookDispatchesListKey(),
        });
        // const key = getWebhookDetailsKey(data.uuid);
        // queryClient.setQueryData(key, data);
        queryOptions?.onSuccess?.(data, requestData, ...rest);
      },
    },
  );
};

//This creates a webhook, overwrites QueryData for that individual webhook,
//and invalidates all related webhook list queries
export const useCreateWebhook: MutationFunction<
  unknown,
  { managedIntegrationUuid: string; body: any }
> = ({ query, requestOptions, queryOptions } = {}) => {
  const queryClient = useQueryClient();
  const request = useRequest(requestOptions);

  return useMutation(
    ({ managedIntegrationUuid, body }) => {
      return request(
        `/managed_integrations/${managedIntegrationUuid}/webhook`,
        {
          query,
          init: { method: 'POST', body },
        },
      );
    },
    {
      ...queryOptions,
      mutationKey: ['createWebhook'],
      onSuccess: (data, requestData, ...rest) => {
        queryClient.invalidateQueries({ queryKey: getWebhookListKey() });
        const key = getWebhookDetailsKey(data?.uuid);
        queryClient.setQueryData(key, data);
        queryOptions?.onSuccess?.(data, requestData, ...rest);
      },
    },
  );
};

//This edits a webhook, overwrites QueryData for that individual webhook,
//and invalidates all related webhook list queries
export const useEditWebhook: MutationFunction<
  unknown,
  { uuid: string; body: any }
> = ({ query, requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);
  const queryClient = useQueryClient();

  return useMutation(
    ({ uuid, body }) => {
      return request(`/managed_integrations/webhook/${uuid}`, {
        query,
        init: { method: 'PATCH', body },
      });
    },
    {
      ...queryOptions,
      mutationKey: ['editWebhook'],
      onSuccess: (data, requestBody, ...rest) => {
        queryClient.invalidateQueries({ queryKey: getWebhookListKey() });
        const key = getWebhookDetailsKey(requestBody.uuid);
        queryClient.setQueryData(key, data);
        queryOptions?.onSuccess?.(data, requestBody, ...rest);
      },
    },
  );
};

//This deletes a webhook and invalidates all related webhook list queries
export const useDeleteWebhook: MutationFunction<
  unknown,
  { webhookUuid: string }
> = ({ query, requestOptions, queryOptions } = {}) => {
  const queryClient = useQueryClient();
  const request = useRequest(requestOptions);

  return useMutation(
    ({ webhookUuid }) => {
      return request(`/managed_integrations/webhook/${webhookUuid}`, {
        query,
        init: { method: 'DELETE' },
      });
    },
    {
      ...queryOptions,
      mutationKey: ['deleteWebhook'],
      onSuccess: (data, requestData, ...rest) => {
        queryClient.invalidateQueries({ queryKey: getWebhookListKey() });
        queryOptions?.onSuccess?.(data, requestData, ...rest);
      },
    },
  );
};

export default {
  useWebhook,
  useWebhookList,
  useWebhookDispatches,
  useWebhookSecret,
  useCreateWebhook,
  useEditWebhook,
  useDeleteWebhook,
  useDispatchDetails,
  useSendDispatch,
};
